import React, { useState, useEffect } from "react";
import { Patient } from "../../PatientCard";
import BodyMapModal from "./BodyMapModal";
import BodyMapService, {
  PatientBodyHistory,
  PatientBodyFinding,
  BodyMapStatistics,
} from "../../../../services/bodyMapService";

interface PatientBodyMapTabProps {
  patient: Patient;
  onDataUpdate?: (tabName: string) => void;
  refreshTrigger?: number;
}

// Group findings by visit date (same date = same visit)
const groupFindingsByVisit = (findings: PatientBodyFinding[]) => {
  const visits: { [date: string]: PatientBodyFinding[] } = {};

  findings.forEach((finding) => {
    // Extract just the date part (YYYY-MM-DD) from the timestamp
    const visitDate = finding.recorded_date.split("T")[0];

    if (!visits[visitDate]) {
      visits[visitDate] = [];
    }
    visits[visitDate].push(finding);
  });

  // Convert to array and sort by date (newest first)
  return Object.entries(visits)
    .map(([date, findings]) => ({
      date,
      findings: findings.sort(
        (a, b) =>
          new Date(b.recorded_date).getTime() -
          new Date(a.recorded_date).getTime()
      ),
    }))
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
};

// Three Dots Options Menu Component
interface FindingOptionsMenuProps {
  finding: PatientBodyFinding;
  onEdit: () => void;
  onDelete: () => void;
  isMobile: boolean;
}

const FindingOptionsMenu: React.FC<FindingOptionsMenuProps> = ({
  finding,
  onEdit,
  onDelete,
  isMobile,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const menuStyle: React.CSSProperties = {
    position: "relative",
    display: "inline-block",
  };

  const buttonStyle: React.CSSProperties = {
    background: "transparent",
    border: "none",
    cursor: "pointer",
    padding: isMobile ? "8px" : "6px",
    borderRadius: "4px",
    fontSize: isMobile ? "16px" : "14px",
    color: "#6b7280",
    transition: "all 0.2s ease",
    minHeight: isMobile ? "32px" : "28px",
    minWidth: isMobile ? "32px" : "28px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  };

  const dropdownStyle: React.CSSProperties = {
    position: "absolute",
    top: "100%",
    right: "0",
    background: "#ffffff",
    border: "1px solid #e5e7eb",
    borderRadius: "6px",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
    zIndex: 1000,
    minWidth: isMobile ? "120px" : "100px",
    overflow: "hidden",
  };

  const optionStyle: React.CSSProperties = {
    padding: isMobile ? "12px 16px" : "8px 12px",
    cursor: "pointer",
    fontSize: isMobile ? "14px" : "13px",
    borderBottom: "1px solid #f3f4f6",
    transition: "background-color 0.2s ease",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    minHeight: isMobile ? "40px" : "32px",
  };

  return (
    <div style={menuStyle}>
      <button
        style={buttonStyle}
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        onMouseEnter={(e) =>
          !isMobile && (e.currentTarget.style.background = "#f3f4f6")
        }
        onMouseLeave={(e) =>
          !isMobile && (e.currentTarget.style.background = "transparent")
        }
      >
        ⋯
      </button>

      {isOpen && (
        <>
          {/* Backdrop to close menu */}
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 999,
            }}
            onClick={() => setIsOpen(false)}
          />

          <div style={dropdownStyle}>
            <div
              className="nn"
              style={optionStyle}
              onClick={(e) => {
                e.stopPropagation();
                onEdit();
                setIsOpen(false);
              }}
              onMouseEnter={(e) =>
                !isMobile && (e.currentTarget.style.background = "#f8fafc")
              }
              onMouseLeave={(e) =>
                !isMobile && (e.currentTarget.style.background = "transparent")
              }
            >
              <span>✏️</span>
              <span>Edit</span>
            </div>
            <div
              style={{
                ...optionStyle,
                borderBottom: "none",
                color: "#dc2626",
              }}
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
                setIsOpen(false);
              }}
              onMouseEnter={(e) =>
                !isMobile && (e.currentTarget.style.background = "#fef2f2")
              }
              onMouseLeave={(e) =>
                !isMobile && (e.currentTarget.style.background = "transparent")
              }
            >
              <span>🗑️</span>
              <span>Delete</span>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// Finding Details Component with Show/Hide Description
interface FindingDetailsProps {
  finding: PatientBodyFinding;
  isMobile: boolean;
}

const FindingDetails: React.FC<FindingDetailsProps> = ({
  finding,
  isMobile,
}) => {
  return (
    <div style={{ padding: isMobile ? "16px" : "12px 16px", flex: 1 }}>
      {/* Description Toggle */}
      {/* {finding.description && (
        <div style={{ marginBottom: "12px" }}>
          <button
            style={{
              background: "transparent",
              border: "1px solid #e5e7eb",
              borderRadius: "4px",
              padding: isMobile ? "8px 12px" : "6px 10px",
              fontSize: isMobile ? "13px" : "12px",
              color: "#6b7280",
              cursor: "pointer",
              transition: "all 0.2s ease",
              width: "100%",
              textAlign: "left",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              minHeight: isMobile ? "36px" : "32px",
            }}
            onClick={() => setShowDescription(!showDescription)}
            onMouseEnter={(e) =>
              !isMobile && (e.currentTarget.style.background = "#f8fafc")
            }
            onMouseLeave={(e) =>
              !isMobile && (e.currentTarget.style.background = "transparent")
            }
          >
            <span>Description</span>
            <span
              style={{
                transform: showDescription ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.2s ease",
              }}
            >
              ▼
            </span>
          </button>

          {showDescription && (
            <div
              style={{
                marginTop: "8px",
                padding: isMobile ? "12px" : "10px",
                background: "#f8fafc",
                borderRadius: "6px",
                border: "1px solid #e5e7eb",
                fontSize: isMobile ? "14px" : "13px",
                color: "#374151",
                lineHeight: "1.4",
                animation: "fadeIn 0.2s ease",
              }}
            >
              {finding.description}
            </div>
          )}
        </div>
      )} */}

      {finding.clinical_notes && (
        <div
          style={{
            background: "#f0f9ff",
            border: "1px solid #dbeafe",
            borderRadius: "6px",
            padding: isMobile ? "10px" : "8px 10px",
            marginBottom: "12px",
          }}
        >
          <div
            style={{
              fontSize: isMobile ? "12px" : "11px",
              fontWeight: "600",
              color: "#1e40af",
              marginBottom: "4px",
            }}
          >
            Clinical Notes:
          </div>
          <div
            style={{
              fontSize: isMobile ? "13px" : "12px",
              color: "#1e40af",
              lineHeight: "1.3",
            }}
          >
            {finding.clinical_notes}
          </div>
        </div>
      )}

      {/* Additional Details Grid */}
      {(finding.size_measurement ||
        finding.color_description ||
        finding.temperature ||
        finding.tenderness ||
        finding.swelling) && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
            gap: "8px",
            fontSize: isMobile ? "12px" : "11px",
            marginBottom: "12px",
            color: "#4b5563",
          }}
        >
          {finding.size_measurement && (
            <div>
              <strong>Size:</strong> {finding.size_measurement}
            </div>
          )}
          {finding.color_description && (
            <div>
              <strong>Color:</strong> {finding.color_description}
            </div>
          )}
          {finding.temperature && (
            <div>
              <strong>Temp:</strong> {finding.temperature}
            </div>
          )}
          {finding.tenderness && (
            <div>
              <strong>Tenderness:</strong> {finding.tenderness}
            </div>
          )}
          {finding.swelling && (
            <div>
              <strong>Swelling:</strong> {finding.swelling}
            </div>
          )}
        </div>
      )}

      {/* Follow-up Info */}
      {finding.follow_up_required && (
        <div
          style={{
            background: "#fef3c7",
            border: "1px solid #f59e0b",
            borderRadius: "6px",
            padding: isMobile ? "10px" : "8px 10px",
            fontSize: isMobile ? "12px" : "11px",
            marginTop: "auto",
          }}
        >
          <strong style={{ color: "#92400e" }}>
            Follow-up required
            {finding.follow_up_date &&
              `: ${BodyMapService.formatDate(finding.follow_up_date)}`}
          </strong>
        </div>
      )}

      {finding.images_attached && (
        <div
          style={{
            marginTop: "8px",
            fontSize: isMobile ? "12px" : "11px",
            color: "#6b7280",
          }}
        >
          {finding.images_count || 0} image(s) attached
        </div>
      )}
    </div>
  );
};

const PatientBodyMapTab: React.FC<PatientBodyMapTabProps> = ({
  patient,
  onDataUpdate,
}) => {
  const [showBodyMapModal, setShowBodyMapModal] = useState(false);
  const [patientHistory, setPatientHistory] =
    useState<PatientBodyHistory | null>(null);
  const [statistics, setStatistics] = useState<BodyMapStatistics | null>(null);
  const [selectedFinding, setSelectedFinding] =
    useState<PatientBodyFinding | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [expandedDescriptions, setExpandedDescriptions] = useState<{
    [key: number]: boolean;
  }>({});
  const [historyFilter, setHistoryFilter] = useState<{
    status: string;
    finding_type: string;
    days: number;
  }>({
    status: "all",
    finding_type: "",
    days: 90,
  });

  // Detect screen sizes
  useEffect(() => {
    const checkScreenSize = () => {
      const mobile = window.innerWidth < 768;
      const tablet = window.innerWidth >= 768 && window.innerWidth < 1024;
      setIsMobile(mobile);
      setIsTablet(tablet);
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  useEffect(() => {
    loadPatientHistory();
    loadStatistics();
  }, [patient.id, refreshTrigger, historyFilter]);

  const loadPatientHistory = async () => {
    setHistoryLoading(true);
    try {
      const history = await BodyMapService.getPatientBodyHistory(patient.id, {
        status:
          historyFilter.status === "all" ? undefined : historyFilter.status,
        finding_type: historyFilter.finding_type || undefined,
        days: historyFilter.days,
        limit: 50,
      });
      setPatientHistory(history);
    } catch (error) {
      console.error("Failed to load patient body history:", error);
    } finally {
      setHistoryLoading(false);
    }
  };

  const loadStatistics = async () => {
    try {
      const stats = await BodyMapService.getStatistics(historyFilter.days);
      setStatistics(stats);
    } catch (error) {
      console.error("Failed to load body map statistics:", error);
      setStatistics(null);
    }
  };

  const handleFindingsSaved = (patientId: number, findingsCount: number) => {
    alert(
      `✅ Body map findings saved successfully! ${findingsCount} finding(s) recorded.`
    );
    setShowBodyMapModal(false);
    setRefreshTrigger((prev) => prev + 1);

    if (onDataUpdate) {
      onDataUpdate("body-mapping");
    }
  };

  const handleUpdateFinding = async (findingId: number, updates: any) => {
    try {
      await BodyMapService.updateFinding(findingId, updates);
      setSelectedFinding(null);
      setRefreshTrigger((prev) => prev + 1);
      // alert("✅ Finding updated successfully!");
    } catch (error: any) {
      alert(`❌ Failed to update finding: ${error.message}`);
    }
  };

  const handleDeleteFinding = async (findingId: number) => {
    if (window.confirm("Are you sure you want to delete this finding?")) {
      try {
        await BodyMapService.deleteFinding(findingId);
        setRefreshTrigger((prev) => prev + 1);
        // alert("✅ Finding deleted successfully!");
      } catch (error: any) {
        alert(`❌ Failed to delete finding: ${error.message}`);
      }
    }
  };

  const toggleDescription = (findingId: number) => {
    setExpandedDescriptions((prev) => ({
      ...prev,
      [findingId]: !prev[findingId],
    }));
  };

  // Flatten all findings from all regions for visit grouping
  const getAllFindings = (): PatientBodyFinding[] => {
    if (!patientHistory?.findings_by_region) return [];

    const allFindings: PatientBodyFinding[] = [];
    Object.values(patientHistory.findings_by_region).forEach((regionData) => {
      allFindings.push(...regionData.findings);
    });

    return allFindings;
  };

  const visitGroups = groupFindingsByVisit(getAllFindings());

  const containerStyle: React.CSSProperties = {
    padding: isMobile ? "16px" : isTablet ? "20px" : "24px",
    height: "100%",
    overflow: "auto",
  };

  const cardStyle: React.CSSProperties = {
    background: "#ffffff",
    borderRadius: isMobile ? "8px" : "12px",
    padding: isMobile ? "16px" : isTablet ? "18px" : "20px",
    marginBottom: isMobile ? "16px" : "24px",
    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
    border: "1px solid #e5e7eb",
  };

  const buttonStyle = (
    variant: "primary" | "secondary" | "danger" = "primary"
  ): React.CSSProperties => ({
    padding: isMobile ? "14px 20px" : isTablet ? "12px 18px" : "12px 24px",
    borderRadius: "8px",
    border: "none",
    fontSize: isMobile ? "16px" : "14px",
    fontWeight: "500",
    cursor: "pointer",
    transition: "all 0.2s ease",
    minHeight: isMobile ? "44px" : "auto",
    background:
      variant === "primary"
        ? "#3b82f6"
        : variant === "danger"
        ? "#ef4444"
        : "#e5e7eb",
    color:
      variant === "primary"
        ? "white"
        : variant === "danger"
        ? "white"
        : "#374151",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
  });

  const filterStyle: React.CSSProperties = {
    padding: isMobile ? "12px" : "8px 12px",
    border: "2px solid #e5e7eb",
    borderRadius: "6px",
    fontSize: isMobile ? "16px" : "14px",
    outline: "none",
    width: "100%",
    minHeight: isMobile ? "44px" : "auto",
  };

  const findingCardStyle: React.CSSProperties = {
    border: "1px solid #e5e7eb",
    borderRadius: "8px",
    overflow: "hidden",
    background: "#ffffff",
    transition: "all 0.2s ease",
    height: "auto",
    display: "flex",
    flexDirection: "column",
  };

  return (
    <>
      <div style={containerStyle}>
        {/* Header */}
        <div style={cardStyle}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: isMobile ? "flex-start" : "center",
              marginBottom: "16px",
              flexDirection: isMobile ? "column" : "row",
              gap: isMobile ? "16px" : "0",
            }}
          >
            <div style={{ width: isMobile ? "100%" : "auto" }}>
              <h3
                style={{
                  fontSize: isMobile ? "18px" : isTablet ? "19px" : "20px",
                  fontWeight: "600",
                  margin: 0,
                  marginBottom: "4px",
                  color: "#1f2937",
                }}
              >
                🗺️ Body Map Examination
              </h3>
              <p
                style={{
                  fontSize: isMobile ? "15px" : "14px",
                  color: "#6b7280",
                  margin: 0,
                  lineHeight: "1.4",
                }}
              >
                Record anatomical findings and examination results for{" "}
                {patient.full_name ||
                  `${patient.first_name} ${patient.last_name}`}
              </p>
            </div>

            <button
              style={{
                ...buttonStyle("primary"),
                width: isMobile ? "100%" : "auto",
                flexShrink: 0,
              }}
              onClick={() => setShowBodyMapModal(true)}
              onMouseEnter={(e) =>
                !isMobile && (e.currentTarget.style.background = "#2563eb")
              }
              onMouseLeave={(e) =>
                !isMobile && (e.currentTarget.style.background = "#3b82f6")
              }
            >
              🗺️ New Body Map Examination
            </button>
          </div>

          {/* Quick Stats */}
          {patientHistory && (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile
                  ? "1fr"
                  : "repeat(auto-fit, minmax(150px, 1fr))",
                gap: "16px",
                padding: "16px",
                background: "#f8fafc",
                borderRadius: "8px",
              }}
            >
              <div style={{ textAlign: "center" }}>
                <div
                  style={{
                    fontSize: isMobile ? "20px" : "24px",
                    fontWeight: "700",
                    color: "#dc2626",
                    marginBottom: "4px",
                  }}
                >
                  {patientHistory.summary.overdue_follow_up}
                </div>
                <div
                  style={{
                    fontSize: isMobile ? "13px" : "12px",
                    color: "#6b7280",
                  }}
                >
                  Overdue Follow-up
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Filters */}
        <div style={cardStyle}>
          <h4
            style={{
              fontSize: isMobile ? "17px" : "16px",
              fontWeight: "600",
              marginBottom: "16px",
              color: "#1f2937",
            }}
          >
            🔍 Filter Body Map History
          </h4>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile
                ? "1fr"
                : isTablet
                ? "repeat(2, 1fr)"
                : "repeat(auto-fit, minmax(200px, 1fr))",
              gap: "16px",
            }}
          >
            <div>
              <label
                style={{
                  display: "block",
                  fontSize: isMobile ? "15px" : "14px",
                  fontWeight: "500",
                  marginBottom: "6px",
                }}
              >
                Status
              </label>
              <select
                value={historyFilter.status}
                onChange={(e) =>
                  setHistoryFilter((prev) => ({
                    ...prev,
                    status: e.target.value,
                  }))
                }
                style={filterStyle}
              >
                <option value="all">All Statuses</option>
                <option value="active">Active</option>
                <option value="resolved">Resolved</option>
                <option value="improving">Improving</option>
                <option value="worsening">Worsening</option>
                <option value="stable">Stable</option>
              </select>
            </div>

            <div>
              <label
                style={{
                  display: "block",
                  fontSize: isMobile ? "15px" : "14px",
                  fontWeight: "500",
                  marginBottom: "6px",
                }}
              >
                Finding Type
              </label>
              <select
                value={historyFilter.finding_type}
                onChange={(e) =>
                  setHistoryFilter((prev) => ({
                    ...prev,
                    finding_type: e.target.value,
                  }))
                }
                style={filterStyle}
              >
                <option value="">All Types</option>
                <option value="injury">Injury</option>
                <option value="symptom">Symptom</option>
                <option value="examination">Examination</option>
                <option value="procedure">Procedure</option>
                <option value="normal">Normal</option>
              </select>
            </div>

            <div>
              <label
                style={{
                  display: "block",
                  fontSize: isMobile ? "15px" : "14px",
                  fontWeight: "500",
                  marginBottom: "6px",
                }}
              >
                Time Period
              </label>
              <select
                value={historyFilter.days}
                onChange={(e) =>
                  setHistoryFilter((prev) => ({
                    ...prev,
                    days: parseInt(e.target.value),
                  }))
                }
                style={filterStyle}
              >
                <option value="30">Last 30 days</option>
                <option value="90">Last 3 months</option>
                <option value="180">Last 6 months</option>
                <option value="365">Last year</option>
                <option value="999999">All time</option>
              </select>
            </div>
          </div>
        </div>

        {/* Body Map History - Grouped by Visits */}
        <div style={cardStyle}>
          <h4
            style={{
              fontSize: isMobile ? "17px" : "16px",
              fontWeight: "600",
              marginBottom: "16px",
              color: "#1f2937",
            }}
          >
            📋 Body Map History by Visits
          </h4>

          {historyLoading ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: isMobile ? "150px" : "200px",
                flexDirection: "column",
                gap: "12px",
              }}
            >
              <div
                style={{
                  width: isMobile ? "32px" : "40px",
                  height: isMobile ? "32px" : "40px",
                  border: "4px solid #e2e8f0",
                  borderTop: "4px solid #3b82f6",
                  borderRadius: "50%",
                  animation: "spin 1s linear infinite",
                }}
              />
              <span style={{ fontSize: isMobile ? "15px" : "14px" }}>
                Loading body map history...
              </span>
            </div>
          ) : !patientHistory || patientHistory.total_findings === 0 ? (
            <div
              style={{
                textAlign: "center",
                padding: isMobile ? "40px 20px" : "60px 20px",
                color: "#6b7280",
              }}
            >
              <div
                style={{
                  fontSize: isMobile ? "32px" : "48px",
                  marginBottom: "16px",
                }}
              >
                🗺️
              </div>
              <h4
                style={{
                  fontSize: isMobile ? "16px" : "18px",
                  marginBottom: "8px",
                }}
              >
                No Body Map Findings
              </h4>
              <p
                style={{
                  fontSize: isMobile ? "15px" : "14px",
                  lineHeight: "1.5",
                }}
              >
                No examination findings recorded for this patient. Start by
                creating a new body map examination.
              </p>
            </div>
          ) : visitGroups.length === 0 ? (
            <div
              style={{
                textAlign: "center",
                padding: isMobile ? "40px 20px" : "60px 20px",
                color: "#6b7280",
              }}
            >
              <div
                style={{
                  fontSize: isMobile ? "32px" : "48px",
                  marginBottom: "16px",
                }}
              >
                🔍
              </div>
              <h4
                style={{
                  fontSize: isMobile ? "16px" : "18px",
                  marginBottom: "8px",
                }}
              >
                No Findings Match Current Filters
              </h4>
              <p
                style={{
                  fontSize: isMobile ? "15px" : "14px",
                  lineHeight: "1.5",
                }}
              >
                Try adjusting your filter criteria to see more results.
              </p>
            </div>
          ) : (
            <div
              style={{
                maxHeight: isMobile ? "500px" : isTablet ? "600px" : "700px",
                overflow: "auto",
              }}
            >
              {visitGroups.map((visit, visitIndex) => (
                <div key={visit.date} style={{ marginBottom: "32px" }}>
                  {/* Visit Header */}
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: isMobile ? "flex-start" : "center",
                      marginBottom: "16px",
                      padding: isMobile ? "12px 16px" : "12px 20px",
                      background: "#f3f4f6",
                      borderRadius: "8px",
                      flexDirection: isMobile ? "column" : "row",
                      gap: isMobile ? "8px" : "0",
                    }}
                  >
                    <div>
                      <h5
                        style={{
                          fontSize: isMobile
                            ? "16px"
                            : isTablet
                            ? "17px"
                            : "18px",
                          fontWeight: "600",
                          color: "#1f2937",
                          margin: 0,
                          marginBottom: "4px",
                        }}
                      >
                        📅 Visit {visitGroups.length - visitIndex} -{" "}
                        {BodyMapService.formatDate(visit.date)}
                      </h5>
                      <div
                        style={{
                          display: "flex",
                          gap: "16px",
                          fontSize: isMobile ? "13px" : "12px",
                          color: "#6b7280",
                          flexDirection: isMobile ? "column" : "row",
                        }}
                      >
                        <span>
                          {visit.findings.length} finding
                          {visit.findings.length !== 1 ? "s" : ""}
                        </span>
                        {visit.findings[0]?.recorded_by_name && (
                          <span>
                            Recorded by: {visit.findings[0].recorded_by_name}
                          </span>
                        )}
                        {visit.findings[0]?.days_ago && (
                          <span>{visit.findings[0].days_ago}</span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Visit Findings - Horizontal Layout */}
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: isMobile
                        ? "1fr"
                        : isTablet
                        ? "repeat(auto-fit, minmax(200px, 1fr))"
                        : "repeat(auto-fit, minmax(250px, 1fr))",
                      gap: isMobile ? "16px" : "20px",
                      gridAutoRows: "min-content",
                    }}
                  >
                    {visit.findings.map((finding) => (
                      <div
                        key={finding.id}
                        style={{
                          ...findingCardStyle,
                          // minHeight: isMobile ? "auto" : "160px",
                        }}
                        onMouseEnter={(e) =>
                          !isMobile &&
                          (e.currentTarget.style.boxShadow =
                            "0 4px 12px rgba(0, 0, 0, 0.15)")
                        }
                        onMouseLeave={(e) =>
                          !isMobile &&
                          (e.currentTarget.style.boxShadow =
                            "0 1px 3px rgba(0, 0, 0, 0.1)")
                        }
                      >
                        {/* Finding Header */}
                        <div
                          style={{
                            background: "#f8fafc",
                            padding: isMobile ? "16px" : "12px 16px",
                            borderBottom: "1px solid #e5e7eb",
                            flexShrink: 0,
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "flex-start",
                              marginBottom: "8px",
                              gap: "8px",
                            }}
                          >
                            <h6
                              style={{
                                fontSize: isMobile ? "15px" : "14px",
                                fontWeight: "600",
                                margin: 0,
                                color: "#1f2937",
                                flex: 1,
                                lineHeight: "1.3",
                              }}
                            >
                              {BodyMapService.getFindingTypeIcon(
                                finding.finding_type
                              )}{" "}
                              {finding.body_location}
                              {finding.laterality_specific &&
                                ` (${finding.laterality_specific})`}
                            </h6>

                            {/* Three Dots Menu */}
                            <FindingOptionsMenu
                              finding={finding}
                              onEdit={() => setSelectedFinding(finding)}
                              onDelete={() => handleDeleteFinding(finding.id!)}
                              isMobile={isMobile}
                            />
                          </div>

                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              gap: isMobile ? "8px" : "12px",
                              flexWrap: isMobile ? "wrap" : "nowrap",
                            }}
                          >
                            {/* Left side - Status and other badges */}
                            <div
                              style={{
                                display: "flex",
                                gap: isMobile ? "6px" : "8px",
                                flexWrap: "wrap",
                                alignItems: "center",
                                flex: 1,
                              }}
                            >
                              <span
                                style={{
                                  padding: "2px 8px",
                                  borderRadius: "12px",
                                  fontSize: isMobile ? "12px" : "11px",
                                  fontWeight: "500",
                                  background:
                                    (finding.status_color ||
                                      BodyMapService.getStatusColor(
                                        finding.status
                                      )) + "20",
                                  color:
                                    finding.status_color ||
                                    BodyMapService.getStatusColor(
                                      finding.status
                                    ),
                                  textTransform: "capitalize",
                                }}
                              >
                                {BodyMapService.getStatusIcon(finding.status)}{" "}
                                {finding.status_label ||
                                  BodyMapService.getStatusLabel(finding.status)}
                              </span>

                              {finding.severity && (
                                <span
                                  style={{
                                    padding: "2px 8px",
                                    borderRadius: "12px",
                                    fontSize: isMobile ? "12px" : "11px",
                                    fontWeight: "500",
                                    background:
                                      (finding.severity_color ||
                                        BodyMapService.getSeverityColor(
                                          finding.severity
                                        )) + "20",
                                    color:
                                      finding.severity_color ||
                                      BodyMapService.getSeverityColor(
                                        finding.severity
                                      ),
                                    textTransform: "capitalize",
                                  }}
                                >
                                  {finding.severity_label ||
                                    BodyMapService.getSeverityLabel(
                                      finding.severity
                                    )}
                                </span>
                              )}

                              {finding.pain_scale !== undefined &&
                                finding.pain_scale > 0 && (
                                  <span
                                    style={{
                                      padding: "2px 8px",
                                      borderRadius: "12px",
                                      fontSize: isMobile ? "12px" : "11px",
                                      fontWeight: "500",
                                      background:
                                        BodyMapService.getPainScaleColor(
                                          finding.pain_scale
                                        ) + "20",
                                      color: BodyMapService.getPainScaleColor(
                                        finding.pain_scale
                                      ),
                                    }}
                                  >
                                    Pain: {finding.pain_scale}/10
                                  </span>
                                )}
                            </div>

                            {/* Right side - Description button */}
                            {finding.description && (
                              <button
                                style={{
                                  background: "transparent",
                                  border: "1px solid #e5e7eb",
                                  borderRadius: "12px",
                                  padding: "2px 8px",
                                  fontSize: isMobile ? "12px" : "11px",
                                  color: "#6b7280",
                                  cursor: "pointer",
                                  transition: "all 0.2s ease",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "4px",
                                  fontWeight: "500",
                                  flexShrink: 0,
                                }}
                                onClick={() => toggleDescription(finding.id!)}
                              >
                                <span>Description</span>
                                <span
                                  style={{
                                    transform: expandedDescriptions[finding.id!]
                                      ? "rotate(180deg)"
                                      : "rotate(0deg)",
                                    transition: "transform 0.2s ease",
                                  }}
                                >
                                  ▼
                                </span>
                              </button>
                            )}
                          </div>

                          {/* Expanded Description */}
                          {expandedDescriptions[finding.id!] &&
                            finding.description && (
                              <div
                                style={{
                                  marginTop: "8px",
                                  padding: isMobile ? "12px" : "10px",
                                  background: "#f8fafc",
                                  borderRadius: "6px",
                                  border: "1px solid #e5e7eb",
                                  fontSize: isMobile ? "14px" : "13px",
                                  color: "#374151",
                                  lineHeight: "1.4",
                                  animation: "fadeIn 0.2s ease",
                                }}
                              >
                                {finding.description}
                              </div>
                            )}

                          {/* Finding Details */}
                          <FindingDetails
                            finding={finding}
                            isMobile={isMobile}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Information Panel */}
        {/* <div
          style={{
            background: "#f0f9ff",
            border: "1px solid #dbeafe",
            borderRadius: "8px",
            padding: isMobile ? "20px" : "16px",
            textAlign: "center",
          }}
        >
          <div
            style={{
              fontSize: isMobile ? "24px" : "20px",
              marginBottom: "8px",
            }}
          >
            🗺️
          </div>
          <h4
            style={{
              fontSize: isMobile ? "17px" : "16px",
              fontWeight: "600",
              marginBottom: "8px",
              color: "#1e40af",
            }}
          >
            Advanced Body Map System
          </h4>
          <p
            style={{
              fontSize: isMobile ? "15px" : "14px",
              color: "#1e40af",
              marginBottom: "16px",
              lineHeight: "1.5",
            }}
          >
            Comprehensive anatomical documentation system for precise medical
            record-keeping. Map examination findings to specific body locations
            with detailed clinical observations.
          </p>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile
                ? "1fr" // Mobile: 1 card per row
                : isTablet
                ? "repeat(auto-fit, minmax(220px, 1fr))" // Tablet: 4 cards (220px min width)
                : "repeat(auto-fit, minmax(350px, 1fr))", // Desktop: 2-3 cards (350px min width)
              gap: isMobile ? "16px" : "20px",
              gridAutoRows: "min-content",
            }}
          >
            <div>Anatomical Location Mapping</div>
            <div>Clinical Finding Documentation</div>
            <div>Pain Scale Assessment</div>
            <div>Follow-up Tracking</div>
            <div>Status & Severity Monitoring</div>
            <div>Historical Progress Views</div>
          </div>
        </div> */}
      </div>

      {/* Body Map Modal */}
      <BodyMapModal
        patient={patient}
        isOpen={showBodyMapModal}
        onClose={() => setShowBodyMapModal(false)}
        onFindingsSaved={handleFindingsSaved}
      />

      {/* Finding Edit Modal */}
      {selectedFinding && (
        <FindingEditModal
          finding={selectedFinding}
          isOpen={!!selectedFinding}
          onClose={() => setSelectedFinding(null)}
          onSave={handleUpdateFinding}
        />
      )}

      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(-4px); }
            to { opacity: 1; transform: translateY(0); }
          }
          
          button:hover:not(:disabled) {
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          }
          
          @media (max-width: 768px) {
            button:hover:not(:disabled) {
              transform: none;
            }
          }
          
          input:focus, select:focus, textarea:focus {
            border-color: #3b82f6 !important;
            box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1) !important;
          }
        `}
      </style>
    </>
  );
};

// Simple Finding Edit Modal Component
interface FindingEditModalProps {
  finding: PatientBodyFinding;
  isOpen: boolean;
  onClose: () => void;
  onSave: (findingId: number, updates: any) => void;
}

const FindingEditModal: React.FC<FindingEditModalProps> = ({
  finding,
  isOpen,
  onClose,
  onSave,
}) => {
  const [description, setDescription] = useState(finding.description);
  const [clinicalNotes, setClinicalNotes] = useState(
    finding.clinical_notes || ""
  );
  const [status, setStatus] = useState(finding.status);
  const [severity, setSeverity] = useState(finding.severity || "");
  const [painScale, setPainScale] = useState(finding.pain_scale || "");
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  if (!isOpen) return null;

  const handleSave = () => {
    onSave(finding.id!, {
      description,
      clinical_notes: clinicalNotes,
      status,
      severity: severity || undefined,
      pain_scale: painScale ? parseInt(painScale.toString()) : undefined,
    });
  };

  const overlayStyle: React.CSSProperties = {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.6)",
    zIndex: 1000,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: isMobile ? "10px" : "20px",
  };

  const modalStyle: React.CSSProperties = {
    backgroundColor: "#ffffff",
    borderRadius: isMobile ? "12px" : "16px",
    width: "100%",
    maxWidth: isMobile ? "100%" : "600px",
    padding: isMobile ? "20px" : "24px",
    boxShadow: "0 25px 50px rgba(0, 0, 0, 0.25)",
    maxHeight: isMobile ? "90vh" : "auto",
    overflow: isMobile ? "auto" : "visible",
  };

  const inputStyle: React.CSSProperties = {
    width: "100%",
    padding: isMobile ? "12px" : "8px 12px",
    border: "2px solid #e5e7eb",
    borderRadius: "6px",
    fontSize: isMobile ? "16px" : "14px",
    outline: "none",
    minHeight: isMobile ? "44px" : "auto",
  };

  const buttonStyle = (
    variant: "primary" | "secondary" = "primary"
  ): React.CSSProperties => ({
    padding: isMobile ? "14px 20px" : "8px 16px",
    borderRadius: "6px",
    border: "none",
    background: variant === "primary" ? "#3b82f6" : "#e5e7eb",
    color: variant === "primary" ? "white" : "#374151",
    fontSize: isMobile ? "16px" : "14px",
    cursor: "pointer",
    flex: isMobile ? "1" : "none",
    minHeight: isMobile ? "44px" : "auto",
  });

  return (
    <div
      style={overlayStyle}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div style={modalStyle}>
        <h3
          style={{
            fontSize: isMobile ? "20px" : "18px",
            fontWeight: "600",
            marginBottom: "16px",
          }}
        >
          Edit Finding: {finding.body_location}
        </h3>

        <div style={{ marginBottom: "16px" }}>
          <label
            style={{
              display: "block",
              fontSize: isMobile ? "15px" : "14px",
              fontWeight: "500",
              marginBottom: "6px",
            }}
          >
            Description
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={isMobile ? 4 : 3}
            style={{ ...inputStyle, resize: "none" }}
          />
        </div>

        <div style={{ marginBottom: "16px" }}>
          <label
            style={{
              display: "block",
              fontSize: isMobile ? "15px" : "14px",
              fontWeight: "500",
              marginBottom: "6px",
            }}
          >
            Clinical Notes
          </label>
          <textarea
            value={clinicalNotes}
            onChange={(e) => setClinicalNotes(e.target.value)}
            rows={isMobile ? 3 : 2}
            style={{ ...inputStyle, resize: "none" }}
          />
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr",
            gap: "16px",
            marginBottom: "20px",
          }}
        >
          <div>
            <label
              style={{
                display: "block",
                fontSize: isMobile ? "15px" : "14px",
                fontWeight: "500",
                marginBottom: "6px",
              }}
            >
              Status
            </label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value as any)}
              style={inputStyle}
            >
              <option value="active">Active</option>
              <option value="resolved">Resolved</option>
              <option value="improving">Improving</option>
              <option value="worsening">Worsening</option>
              <option value="stable">Stable</option>
            </select>
          </div>

          <div>
            <label
              style={{
                display: "block",
                fontSize: isMobile ? "15px" : "14px",
                fontWeight: "500",
                marginBottom: "6px",
              }}
            >
              Severity
            </label>
            <select
              value={severity}
              onChange={(e) => setSeverity(e.target.value)}
              style={inputStyle}
            >
              <option value="">Select severity</option>
              <option value="mild">Mild</option>
              <option value="moderate">Moderate</option>
              <option value="severe">Severe</option>
              <option value="critical">Critical</option>
            </select>
          </div>

          <div>
            <label
              style={{
                display: "block",
                fontSize: isMobile ? "15px" : "14px",
                fontWeight: "500",
                marginBottom: "6px",
              }}
            >
              Pain Scale (0-10)
            </label>
            <input
              type="number"
              id="pain_scale"
              name="pain_scale"
              min="0"
              max="10"
              value={painScale}
              onChange={(e) => setPainScale(e.target.value)}
              style={inputStyle}
            />
          </div>
        </div>

        <div
          style={{
            display: "flex",
            gap: "12px",
            justifyContent: "flex-end",
            flexDirection: isMobile ? "column" : "row",
          }}
        >
          <button onClick={onClose} style={buttonStyle("secondary")}>
            Cancel
          </button>
          <button onClick={handleSave} style={buttonStyle("primary")}>
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
};

export default PatientBodyMapTab;
